import { ChangeEvent, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import classNames from 'classnames'
import { AutoComplete, Field } from '@matillion/component-library'

import { useGetEnvironments } from 'api/hooks'
import { renderFormikError } from 'modules/utils'
import classes from 'modules/FormsContent/Form.module.scss'
import { AutoCompleteProps } from 'types/FormTypes'
import { CommonFieldsTypes } from 'modules/Common/Fields/types'

export interface EnvironmentFormikContext {
  environment: AutoCompleteProps
}

export const Environment = ({
  'data-testid': dataTestId = 'common-field-environment',
  onChange
}: CommonFieldsTypes) => {
  const { projectId } = useParams()

  const { errors, handleBlur, handleChange, touched, values } =
    useFormikContext<EnvironmentFormikContext>()
  const { t } = useTranslation()
  const {
    data: listOfEnvironments = [],
    isError: environmentIsError,
    isLoading: environmentIsLoading
  } = useGetEnvironments(projectId!, { retryOnMount: false })

  const environmentError = renderFormikError(
    errors.environment,
    Boolean(touched.environment)
  )

  const getErrorMessage = useMemo(() => {
    if (environmentIsError) {
      return t('commonForm.fields.environment.error.loadingError')
    } else if (!listOfEnvironments.length) {
      return t('commonForm.fields.environment.error.noItemsFound')
    } else if (environmentError) {
      return t(environmentError)
    }

    return ''
  }, [environmentError, environmentIsError, listOfEnvironments.length, t])

  return (
    <div data-testid={dataTestId}>
      <Field
        inputComponent={AutoComplete}
        loading={environmentIsLoading}
        availableItems={listOfEnvironments
          .map((x) => ({ id: x.id, name: x.name }))
          .sort((a, b) => a.name.localeCompare(b.name))}
        title={t('commonForm.fields.environment.title')}
        name="environment"
        data-testid={`${dataTestId}-input`}
        value={values.environment}
        placeholder={
          environmentIsError
            ? t('commonForm.fields.environment.error.placeholderText')
            : t('commonForm.fields.environment.placeholderText')
        }
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          handleChange(e)
          if (onChange) {
            onChange(e)
          }
        }}
        onBlur={handleBlur}
        className={classNames(classes.Form__Field)}
        supportText={t('commonForm.fields.environment.supportText')}
        errorText={environmentIsLoading ? undefined : getErrorMessage}
        hasError={
          Boolean(errors?.environment?.id) &&
          Boolean(touched.environment) &&
          !environmentIsLoading
        }
        required
        disabled={environmentIsError || !listOfEnvironments.length}
      />
    </div>
  )
}
